import type { Locale } from '../translations';
import { DEFAULT_SITE_NAME } from '../constants/brand';

/** @deprecated Use getSeoKeywords(locale) from content/seo.ts */
export const SEO_KEYWORDS =
    'empire icarus, empire icarus online, icarus online, riders of icarus, icarus kr, icarus mmorpg, dragon mount mmo, tame dragons, familiars, Hakanas, MMORPG mount combat';

export { getSeoKeywords, SEO_KEYWORDS_BY_LOCALE } from './seo';

export const SEO_META = {
    en: {
        gamePageTitle: 'Game Guide',
        gamePageDescription:
            'Empire Icarus MMORPG guide: tame dragons and familiars, mounted combat, 8 classes, dungeons, PvP and Hakanas. Icarus Online / Riders of Icarus / Icarus KR players welcome.',
        homeDescription:
            'Empire Icarus — free dragon-mount MMORPG. Tame familiars, fight from the saddle, eight classes, dungeons and rankings.',
        classesDescription:
            'All 8 Empire Icarus classes: Assassin, Archer, Mage, Berserker, Guardian, Trickster, Magician and Priest.',
    },
    'pt-BR': {
        gamePageTitle: 'Guia do jogo',
        gamePageDescription:
            'Guia Empire Icarus: domar dragões e familiares, combate montado, 8 classes, masmorras, PvP e Hakanas. Jogadores de Icarus Online / Riders of Icarus / Icarus KR.',
        homeDescription:
            'Empire Icarus — MMORPG gratuito com montarias e dragões. Doma, combate aéreo, oito classes, masmorras e rankings.',
        classesDescription:
            'As 8 classes do Empire Icarus: Assassino, Arqueiro, Mago, Berserker, Guardian, Trickster, Magician e Priest.',
    },
} as const;

type GameSection = { id: string; title: string; paragraphs: string[]; bullets?: string[] };

export type GamePageContent = {
    seo: { title: string; description: string };
    hero: { badge: string; title: string; highlight: string; subtitle: string };
    sections: GameSection[];
    classesTeaser: { title: string; text: string; cta: string };
    server: { title: string; paragraphs: string[]; bullets: string[] };
    faq: { q: string; a: string }[];
    cta: { title: string; download: string; register: string };
};

const en: GamePageContent = {
    seo: {
        title: SEO_META.en.gamePageTitle,
        description: SEO_META.en.gamePageDescription,
    },
    hero: {
        badge: 'MMORPG · Mount combat · Dragon taming',
        title: DEFAULT_SITE_NAME,
        highlight: 'Game guide',
        subtitle:
            'Learn how our world works: tame familiars, fight mounted, pick your class, and progress through dungeons, PvP, and guild content.',
    },
    sections: [
        {
            id: 'overview',
            title: 'About Empire Icarus',
            paragraphs: [
                'Empire Icarus is a fantasy MMORPG built around Riders who bond with familiars — ground mounts, flying beasts, and dragons. Combat, exploration, and progression are designed around your mount, not just your character.',
                'Create a free account, download the client, and join a community with rankings, seasonal rewards, cash shop cosmetics, and regular news and events.',
            ],
        },
        {
            id: 'mounts',
            title: 'Familiars, dragons & mounted combat',
            paragraphs: [
                'In the open world you weaken creatures, complete taming, and contract them as mounts or battle partners. Loyalty and level unlock stronger mount skills.',
                'Mounted combat adds aerial and ground tactics: positioning, mount choice, and skill timing decide PvE bosses and PvP encounters.',
            ],
            bullets: [
                'Tame familiars across multiple regions',
                'Ground and flying mounts, including dragons',
                'Dedicated mount weapons and skills',
                'Collection and loyalty progression',
            ],
        },
        {
            id: 'world',
            title: 'World & capital',
            paragraphs: [
                'Your adventure begins in Hakanas, the central hub for trade, quests, and grouping. Beyond the capital lie zones with unique bosses, taming targets, and dungeon entrances.',
                'World bosses, seasonal events, and guild activities keep the world active for solo players and parties alike.',
            ],
        },
        {
            id: 'gameplay',
            title: 'Dungeons, PvP & guilds',
            paragraphs: [
                'Classic MMORPG loops: quests, gear, crafting, and instanced dungeons in normal and hard modes. Guilds run cooperative content and competitive wars.',
                'Climb PvP and PvE rankings for seasonal titles and rewards. Customize your Rider with costumes and mount skins from the cash shop.',
            ],
            bullets: [
                'Party dungeons and raids',
                'Open-world and structured PvP',
                'Guild quests and guild wars',
                'Seasonal leaderboards',
            ],
        },
    ],
    classesTeaser: {
        title: 'Eight playable classes',
        text: 'Assassin, Archer, Mage, Berserker, Guardian, Trickster, Magician, and Priest — each with a distinct role in parties and PvP.',
        cta: 'View all classes',
    },
    server: {
        title: 'Start playing',
        paragraphs: [
            'Register for free, download the client from our Download page, and manage characters, GCoins, and rankings from your account panel.',
        ],
        bullets: [
            'Free registration',
            'Rankings and cash shop (GCoins)',
            'News and patch notes on-site',
            'Discord support and community',
        ],
    },
    faq: [
        {
            q: 'Is Empire Icarus free to play?',
            a: 'Yes. Registration and download are free. Optional donations and GCoins support the server and cosmetics.',
        },
        {
            q: 'What makes mounted combat special?',
            a: 'You fight while mounted using mount skills and positioning — especially in aerial PvP and boss encounters.',
        },
        {
            q: 'How many classes are there?',
            a: 'Eight classes, each with unique skills for dungeons and PvP. See the Classes page for details.',
        },
        {
            q: 'Where do I download the client?',
            a: 'Go to Download on this site for the installer and setup steps.',
        },
        {
            q: 'How do I get support?',
            a: 'Join our Discord for tickets and help from the team and community.',
        },
    ],
    cta: {
        title: 'Ready to ride?',
        download: 'Download client',
        register: 'Create account',
    },
};

const ptBR: GamePageContent = {
    seo: {
        title: SEO_META['pt-BR'].gamePageTitle,
        description: SEO_META['pt-BR'].gamePageDescription,
    },
    hero: {
        badge: 'MMORPG · Combate montado · Doma de dragões',
        title: DEFAULT_SITE_NAME,
        highlight: 'Guia do jogo',
        subtitle:
            'Entenda o mundo do jogo: domar familiares, lutar montado, escolher sua classe e evoluir em masmorras, PvP e guildas.',
    },
    sections: [
        {
            id: 'overview',
            title: 'Sobre o Empire Icarus',
            paragraphs: [
                'Empire Icarus é um MMORPG de fantasia em que Riders criam vínculo com familiares — montarias terrestres, voadoras e dragões. Combate e progressão giram em torno da sua montaria, não só do personagem.',
                'Crie conta grátis, baixe o cliente e participe de uma comunidade com rankings, recompensas sazonais, cosméticos na cash shop e notícias frequentes.',
            ],
        },
        {
            id: 'mounts',
            title: 'Familiares, dragões e combate montado',
            paragraphs: [
                'No mundo aberto você enfraquece criaturas, completa a doma e as contrata como montaria ou parceiro de batalha. Lealdade e nível liberam skills mais fortes.',
                'O combate montado adiciona tática aérea e terrestre: posicionamento, escolha da montaria e timing das habilidades definem chefes e PvP.',
            ],
            bullets: [
                'Dome familiares em várias regiões',
                'Montarias terrestres e voadoras, incluindo dragões',
                'Armas e skills dedicadas à montaria',
                'Coleção e progressão de lealdade',
            ],
        },
        {
            id: 'world',
            title: 'Mundo e capital',
            paragraphs: [
                'A aventura começa em Hakanas, hub central para trade, quests e grupos. Além da capital há zonas com bosses, alvos de doma e masmorras.',
                'World bosses, eventos sazonais e atividades de guilda mantêm o mundo ativo para solo e grupos.',
            ],
        },
        {
            id: 'gameplay',
            title: 'Masmorras, PvP e guildas',
            paragraphs: [
                'Progressão clássica de MMORPG: quests, gear, crafting e masmorras instanciadas em modos normal e difícil. Guildas fazem conteúdo cooperativo e guerras.',
                'Suba nos rankings de PvP e PvE por títulos e recompensas sazonais. Personalize seu Rider com visuais e skins de montaria.',
            ],
            bullets: [
                'Masmorras e raids em grupo',
                'PvP open-world e estruturado',
                'Quests e guerras de guilda',
                'Leaderboards sazonais',
            ],
        },
    ],
    classesTeaser: {
        title: 'Oito classes jogáveis',
        text: 'Assassino, Arqueiro, Mago, Berserker, Guardian, Trickster, Magician e Priest — cada um com papel distinto em grupo e PvP.',
        cta: 'Ver todas as classes',
    },
    server: {
        title: 'Comece a jogar',
        paragraphs: [
            'Registre-se grátis, baixe o cliente na página Download e gerencie personagens, GCoins e rankings no painel da conta.',
        ],
        bullets: [
            'Registro gratuito',
            'Rankings e cash shop (GCoins)',
            'Notícias e patches no site',
            'Suporte e comunidade no Discord',
        ],
    },
    faq: [
        {
            q: 'Empire Icarus é gratuito?',
            a: 'Sim. Registro e download são grátis. Doações e GCoins opcionais ajudam o servidor e cosméticos.',
        },
        {
            q: 'O que torna o combate montado especial?',
            a: 'Você luta montado com skills da montaria e posicionamento — especialmente em PvP aéreo e chefes.',
        },
        {
            q: 'Quantas classes existem?',
            a: 'Oito classes com skills únicas. Veja a página Classes para detalhes.',
        },
        {
            q: 'Onde baixo o cliente?',
            a: 'Acesse Download neste site para o instalador e o passo a passo.',
        },
        {
            q: 'Como obter suporte?',
            a: 'Entre no Discord para tickets e ajuda da equipe e da comunidade.',
        },
    ],
    cta: {
        title: 'Pronto para voar?',
        download: 'Baixar cliente',
        register: 'Criar conta',
    },
};

export function getGamePageContent(locale: Locale): GamePageContent {
    return locale === 'pt-BR' ? ptBR : en;
}

export function getSeoMeta(locale: Locale) {
    return locale === 'pt-BR' ? SEO_META['pt-BR'] : SEO_META.en;
}

export function buildGameJsonLd(siteName: string, siteUrl: string, locale: Locale) {
    const c = getGamePageContent(locale);
    return [
        {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: siteName,
            url: siteUrl,
            description: c.seo.description,
        },
        {
            '@context': 'https://schema.org',
            '@type': 'VideoGame',
            name: siteName,
            alternateName: ['Empire Icarus', 'Icarus Online', 'Riders of Icarus', 'Icarus KR'],
            description: c.seo.description,
            genre: ['MMORPG', 'Action', 'Fantasy'],
            gamePlatform: 'PC',
            applicationCategory: 'Game',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        },
        {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: c.faq.map((item) => ({
                '@type': 'Question',
                name: item.q,
                acceptedAnswer: { '@type': 'Answer', text: item.a },
            })),
        },
    ];
}
